import React, { useState, useEffect, useCallback } from 'react';
import { InventoryEvent } from '../../types/inventory';
import { inventoryAPI } from '../../services/inventoryApi';
import { formatRelativeDate } from '../../utils/dateFormat';

const EVENT_ICONS: Record<string, string> = {
  compound_registered: '\u{1F9EA}',
  batch_created: '\u{1F3ED}',
  sample_created: '\u{1F9F4}',
  sample_dispensed: '\u{1F4A7}',
  sample_moved: '\u{1F4E6}',
  sample_reserved: '\u{1F512}',
  sample_released: '\u{1F513}',
  location_created: '\u{1F4CD}',
  location_deleted: '\u{1F5D1}\u{FE0F}',
};

const EVENT_LABELS: Record<string, string> = {
  compound_registered: 'Compound registered',
  batch_created: 'Batch created',
  sample_created: 'Sample created',
  sample_dispensed: 'Sample dispensed',
  sample_moved: 'Sample moved',
  sample_reserved: 'Sample reserved',
  sample_released: 'Reservation released',
  location_created: 'Location created',
  location_deleted: 'Location deleted',
};

const ActivityFeed: React.FC = () => {
  const [events, setEvents] = useState<InventoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;

  const loadEvents = useCallback((append = false) => {
    const currentOffset = append ? offset : 0;
    setLoading(true);
    inventoryAPI.getEvents({ limit, offset: currentOffset })
      .then(res => {
        const data = res.data;
        if (append) {
          setEvents(prev => [...prev, ...data]);
        } else {
          setEvents(data);
        }
        setHasMore(data.length === limit);
        setOffset(currentOffset + data.length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [offset]);

  useEffect(() => { loadEvents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ padding: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>
        Activity Log
      </h3>

      {events.length === 0 && !loading ? (
        <div style={{
          padding: 40, textAlign: 'center', color: 'var(--text-secondary)',
          border: '1px dashed var(--card-border)', borderRadius: 8,
        }}>
          No activity yet. Register a compound to get started.
        </div>
      ) : (
        <div style={{
          border: '1px solid var(--card-border)', borderRadius: 8,
          overflow: 'hidden',
        }}>
          {events.map((event, i) => (
            <div key={event.event_id} style={{
              padding: '12px 16px',
              borderBottom: i < events.length - 1 ? '1px solid var(--card-border)' : 'none',
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>
                {EVENT_ICONS[event.event_type] || '\u{1F4CB}'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                  {EVENT_LABELS[event.event_type] || event.event_type.replace(/_/g, ' ')}
                </div>
                {event.details && Object.keys(event.details).length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
                    {formatDetails(event.details)}
                  </div>
                )}
              </div>
              <div style={{
                fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0,
                whiteSpace: 'nowrap',
              }}>
                {formatRelativeDate(event.performed_at)}
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-secondary)' }}>
          Loading...
        </div>
      )}

      {!loading && hasMore && events.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button
            onClick={() => loadEvents(true)}
            style={{
              padding: '8px 20px', borderRadius: 6,
              border: '1px solid var(--card-border)',
              backgroundColor: 'transparent', color: 'var(--text-secondary)',
              fontSize: 12, cursor: 'pointer',
            }}
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

function formatDetails(details: Record<string, any>): string {
  const parts: string[] = [];
  if (details.chemistry_code) parts.push(details.chemistry_code);
  if (details.batch_code) parts.push(details.batch_code);
  if (details.barcode) parts.push(details.barcode);
  if (details.amount_dispensed != null) parts.push(`-${details.amount_dispensed} mg`);
  if (details.inchi_key) parts.push(details.inchi_key.substring(0, 14) + '...');
  if (details.mol_formula) parts.push(details.mol_formula);
  if (details.name) parts.push(details.name);
  if (details.notes) parts.push(details.notes);
  return parts.join(' \u00B7 ') || '';
}

export default ActivityFeed;
